/**
 * AttachEvidence – Standalone lightweight page for mobile share-sheet handoff (Pathway B).
 * Route: /attach
 *
 * Flow: user shares a file from camera/gallery → PWA catches it → lands here.
 * User picks a recent ticket (or skips), selects a role, and uploads.
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useUploadArtifact } from '@/hooks/useArtifacts';
import { ARTIFACT_SOURCES, LINK_ROLES, LINK_ENTITY_TYPES } from '@/lib/artifacts/constants';
import type { LinkRole } from '@/lib/artifacts/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Camera, Upload, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function AttachEvidence() {
  const { activeShop } = useAuth();
  const navigate = useNavigate();
  const uploadMutation = useUploadArtifact();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string>('');
  const [ticketSearch, setTicketSearch] = useState('');
  const [role, setRole] = useState<LinkRole>(LINK_ROLES.REFERENCE);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  // Fetch recent tickets for selector
  const { data: recentTickets } = useQuery({
    queryKey: ['recent-tickets-for-attach', activeShop?.id, ticketSearch],
    enabled: !!activeShop?.id,
    queryFn: async () => {
      let query = supabase
        .from('tickets')
        .select('id, customer_name, vehicle_year, vehicle_make, vehicle_model')
        .eq('shop_id', activeShop!.id)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false })
        .limit(10);

      if (ticketSearch.trim()) {
        query = query.or(
          `id.ilike.%${ticketSearch}%,customer_name.ilike.%${ticketSearch}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith('image/')) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !activeShop?.id) return;
    setUploading(true);
    try {
      await uploadMutation.mutateAsync({
        shopId: activeShop.id,
        file,
        source: ARTIFACT_SOURCES.SHARE_SHEET,
        notes: notes || undefined,
        link: ticketId
          ? {
              entityType: LINK_ENTITY_TYPES.TICKET,
              entityId: ticketId,
              role,
            }
          : undefined,
      });
      setDone(true);
      toast.success('Evidence uploaded successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!activeShop) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <p className="text-muted-foreground">Loading shop…</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="pt-8 pb-6 space-y-4">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <h2 className="text-lg font-semibold">Uploaded!</h2>
            <p className="text-sm text-muted-foreground">
              {ticketId
                ? `Linked to ticket ${ticketId}`
                : 'Saved to your artifact inbox'}
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <Button variant="outline" size="sm" onClick={() => {
                setFile(null);
                setPreview(null);
                setTicketId('');
                setNotes('');
                setDone(false);
              }}>
                Attach Another
              </Button>
              <Button size="sm" onClick={() => navigate('/')}>
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">Attach Evidence</h1>
        </div>

        {/* File picker */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Photo / File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,application/pdf,.pdf"
              capture="environment"
              className="hidden"
              onChange={handleFileSelect}
            />
            {file ? (
              <div className="space-y-2">
                {preview ? (
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full rounded-md max-h-48 object-cover"
                  />
                ) : (
                  <div className="bg-muted rounded-md p-4 text-center text-sm text-muted-foreground">
                    {file.name}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Change File
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4" />
                  Camera
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.removeAttribute('capture');
                      fileInputRef.current.click();
                      fileInputRef.current.setAttribute('capture', 'environment');
                    }
                  }}
                >
                  <Upload className="h-4 w-4" />
                  Browse
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket selector */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Link to Ticket</CardTitle>
            <CardDescription className="text-xs">
              Optional — skip to save to inbox
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Search tickets…"
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
            />
            {recentTickets && recentTickets.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {recentTickets.map((t: any) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTicketId(t.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      ticketId === t.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <span className="font-medium">{t.id}</span>
                    <span className="text-xs ml-2 opacity-80">
                      {t.customer_name} • {t.vehicle_year} {t.vehicle_make} {t.vehicle_model}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {ticketId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTicketId('')}
                className="text-xs"
              >
                Clear selection
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Role + notes */}
        {ticketId && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Role</Label>
                <Select value={role} onValueChange={(v) => setRole(v as LinkRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={LINK_ROLES.DIAGNOSTIC_EVIDENCE}>Diagnostic Evidence</SelectItem>
                    <SelectItem value={LINK_ROLES.INSPECTION_EVIDENCE}>Inspection Evidence</SelectItem>
                    <SelectItem value={LINK_ROLES.COMPLAINT_SUPPORT}>Complaint Support</SelectItem>
                    <SelectItem value={LINK_ROLES.NOTE_ATTACHMENT}>Note Attachment</SelectItem>
                    <SelectItem value={LINK_ROLES.REFERENCE}>Reference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-1">
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea
            placeholder="Add context…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </div>

        {/* Submit */}
        <Button
          className="w-full"
          disabled={!file || uploading}
          onClick={handleUpload}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Uploading…
            </>
          ) : (
            'Upload Evidence'
          )}
        </Button>
      </div>
    </div>
  );
}
